import React, { useState } from 'react';
import { Clock, Search, ArrowUp } from 'lucide-react';
import Introduction from './privacy/Introduction';
import DataCollection from './privacy/DataCollection';
import DataUsage from './privacy/DataUsage';
import DataStorage from './privacy/DataStorage';
import DataSharing from './privacy/DataSharing';
import UserRights from './privacy/UserRights';
import EmotionalContent from './privacy/EmotionalContent';
import International from './privacy/International';
import Cookies from './privacy/Cookies';
import Marketing from './privacy/Marketing';
import DataBreach from './privacy/DataBreach';
import Updates from './privacy/Updates';
import Contact from './privacy/Contact';
import Regulatory from './privacy/Regulatory';

interface Section {
  id: string;
  title: string;
  component: React.ReactNode;
}

const Privacy: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Sections data
  const sections: Section[] = [
    {
      id: 'introduction',
      title: 'Introduction & Overview',
      component: <Introduction />
    },
    {
      id: 'data-collection',
      title: 'Information We Collect',
      component: <DataCollection />
    },
    {
      id: 'data-usage',
      title: 'How We Use Your Information',
      component: <DataUsage />
    },
    {
      id: 'data-storage',
      title: 'Data Storage & Security',
      component: <DataStorage />
    },
    {
      id: 'data-sharing',
      title: 'Data Sharing & Third Parties',
      component: <DataSharing />
    },
    {
      id: 'user-rights',
      title: 'User Rights & Controls',
      component: <UserRights />
    },
    {
      id: 'emotional-content',
      title: 'Special Protection for Emotional Content',
      component: <EmotionalContent />
    },
    {
      id: 'international',
      title: 'International Data Protections',
      component: <International />
    },
    {
      id: 'cookies',
      title: 'Cookies & Tracking Technologies',
      component: <Cookies />
    },
    {
      id: 'marketing',
      title: 'Marketing Communications',
      component: <Marketing />
    },
    {
      id: 'data-breach',
      title: 'Data Breach Procedures',
      component: <DataBreach />
    },
    {
      id: 'updates',
      title: 'Privacy Policy Updates',
      component: <Updates />
    },
    {
      id: 'contact',
      title: 'Data Protection Contact',
      component: <Contact />
    },
    {
      id: 'regulatory',
      title: 'Regulatory Information',
      component: <Regulatory />
    }
  ];

  // Handle scroll events for back-to-top button
  React.useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Handle smooth scroll for anchor links
  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const target = e.currentTarget;
    if (target.hash) {
      e.preventDefault();
      
      // Get the target element
      const targetElement = document.querySelector(target.hash);
      if (targetElement) {
        // Calculate position with offset for header (adjust 100px as needed)
        const offsetPosition = targetElement.getBoundingClientRect().top + 
          window.pageYOffset - 100;
        
        // Scroll to the adjusted position
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        
        // Update URL without triggering scroll
        window.history.pushState(null, '', target.hash);
      }
    }
  };
  
  // Handle initial hash on page load
  React.useEffect(() => {
    // Get the hash from the URL
    const hash = window.location.hash;
    
    // Handle initial load with hash
    if (hash) {
      setTimeout(() => {
        const targetElement = document.querySelector(hash);
        if (targetElement) {
          const offsetPosition = targetElement.getBoundingClientRect().top + 
            window.pageYOffset - 100;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Filter sections based on search
  const filteredSections = sections.filter(section =>
    section.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      <div className="container mx-auto px-4 md:px-6 py-12">
        {/* Header */}
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 font-['Poppins']">
            Privacy Policy
          </h1>
          
          <div className="flex items-center gap-4 text-gray-600 mb-8">
            <div className="flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              <span>12 min read</span>
            </div>
            <div className="h-1 w-1 bg-gray-300 rounded-full"></div>
            <span>Last updated: March 15, 2025</span>
          </div>

          {/* Search */}
          <div className="relative mb-12">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search privacy policy..."
              className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#C2A4D6] focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Table of Contents */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-12 table-of-contents">
            <h2 className="text-xl font-semibold mb-4">Table of Contents</h2>
            <ul className="space-y-2">
              {sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="text-[#C2A4D6] hover:text-[#B390C9] transition-colors"
                    onClick={handleAnchorClick}
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            {filteredSections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                className="bg-white rounded-xl shadow-md p-6 md:p-8"
              >
                <h2 className="text-2xl font-semibold mb-6">{section.title}</h2>
                {section.component}
              </section>
            ))}
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-[#C2A4D6] hover:bg-[#B390C9] text-white p-3 rounded-full shadow-lg transition-colors"
          aria-label="Back to top"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default Privacy;